import React, { useCallback, useMemo, useRef, useState } from 'react';
import FsLightbox from 'fslightbox-react';
import classNames from 'classnames';
import { getImageUrl } from '@common/utils/image';
import { IImage } from "@common/redux/types/images";
import { ISrcSet, Picture } from "@kursk/components/ui/Picture/Picture";
import { PortalImage } from "@kursk/components/ui/PortalImage/PortalImage";
import { AuthorInfo } from "@kursk/components/ui/AuthorInfo/AuthorInfo";
import { useMutationObservable } from "@common/hooks/useMutationObservable";
import styles from './ImageWithInfo.module.scss';

interface ImageProps {
  image: IImage,
  width?: number,
  height?: number,
  modifiers?: string[];
  srcSet: ISrcSet[];
  useFullscreen?: boolean;
  className?: string;
  carouselSlides?: IImage[];
}

export const ImageWithInfo = (
  { image, srcSet, modifiers = [], useFullscreen = false, className = '', carouselSlides = [] }: ImageProps) => {
  const ref = useRef();
  const [fullscreen, setFullscreen] = useState<boolean>(false);
  const [showDescription, setShowDescription] = useState<boolean>(false)
  const [slide, setSlide] = useState<number>(null);
  const [description, setDescription] = useState<string>(null);

  const setCarouselImageState = () => {
    setFullscreen(!fullscreen)
    if (carouselSlides.length > 0) setSlide(carouselSlides.indexOf(image) + 1)
    setShowDescription(!showDescription)
    setDescription(image.description)
  }

  const onILightBoxClose = () => {
    setShowDescription(!showDescription)
  }

  /* Когда происходит листание картинки, то происходит мутация целевого узла,
  а именно, меняется текстовое значение счетчика картинки, которое расположено
  в левом верхнем углу. Таким образом мы можем отследить изменение значения счетчика,
  что позволяет рассчитать значение индекса картинки в carouselSlides и его description*/
  const onLightBoxMutation = useCallback((mutationList) => {
    mutationList.forEach((mutation) => {
      if (mutation.type === 'characterData' && carouselSlides.length > 0) {
        setDescription(carouselSlides[parseInt(mutation.target.data) - 1].description)
      }
    })
  }, [carouselSlides])

  const LightBoxComponent = useMemo(() => {
    return (
      <FsLightbox
        toggler={fullscreen}
        type="image"
        sources={carouselSlides.length > 0 ? carouselSlides.map(slide => getImageUrl(slide)) : [getImageUrl(image)]}
        slide={carouselSlides.length > 0 ? slide : 1}
        onClose={onILightBoxClose}
      />
    )
  }, [slide, carouselSlides, fullscreen])

  const DescriptionComponent = useMemo(() => {
    return (
      <div className={styles.carouselImageDescription}>
        {description}
      </div>
    )
  }, [description])

  if (carouselSlides.length > 0) useMutationObservable({ targetEl: ref.current, cb: onLightBoxMutation });

  if (!image) {
    return <></>
  }

  return (
    <div className={className}>
      <AuthorInfo image={image}/>
      <div onClick={setCarouselImageState}
        className={classNames([useFullscreen && styles.pointer], styles.fullHeight)}>
        <Picture
          className={classNames(modifiers.map(m => styles[m]), styles.fullHeight)}
          srcSet={srcSet}
          image={image}
        />
      </div>
      {useFullscreen && <PortalImage>
        <div ref={ref}>
          {LightBoxComponent}
        </div>
        {showDescription && description && DescriptionComponent}
      </PortalImage>}
    </div>
  );
};
